import React from "react";
import { Link } from "react-router-dom";
import { Bus, ShieldCheck, Radar, QrCode, MapPin, BellRing, ArrowRight, Cpu } from "lucide-react";

const Stat = ({ label, value }) => (
  <div className="border border-zinc-900 p-6 bg-white">
    <div className="text-[11px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{label}</div>
    <div className="mt-2 font-display font-black text-4xl tracking-tighter">{value}</div>
  </div>
);

const Feature = ({ icon: Icon, title, desc, span = "md:col-span-4", dark = false }) => (
  <div className={`border border-zinc-900 p-6 sm:p-8 ${span} ${dark ? "bg-zinc-900 text-white" : "bg-white"} flex flex-col justify-between min-h-[220px]`}>
    <div>
      <Icon size={28} className={dark ? "text-[#FFC60A]" : "text-zinc-900"} />
      <h3 className="mt-5 font-display font-bold text-xl sm:text-2xl tracking-tight">{title}</h3>
      <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-zinc-300" : "text-zinc-600"}`}>{desc}</p>
    </div>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* NAV */}
      <header className="sticky top-0 z-40 border-b border-zinc-900 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-4 sm:px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="w-8 h-8 bg-[#FFC60A] border border-zinc-900 flex items-center justify-center">
              <Bus size={18} className="text-zinc-900" />
            </div>
            <div className="font-display font-black text-xl tracking-tighter">LOGIBUS</div>
          </Link>
          <nav className="hidden md:flex gap-8 text-sm font-medium">
            <a href="#features" className="hover:text-zinc-500">Modules</a>
            <a href="#how" className="hover:text-zinc-500">Comment ça marche</a>
            <a href="#roles" className="hover:text-zinc-500">Rôles</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-login-link" className="hidden sm:inline-flex text-sm font-semibold px-4 py-2 border border-zinc-900 hover:bg-zinc-100">
              Connexion
            </Link>
            <Link to="/register" data-testid="landing-register-cta" className="text-sm font-bold px-4 py-2 bg-[#FFC60A] border border-zinc-900 hover:bg-[#ffd43a] inline-flex items-center gap-1">
              Démarrer <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-zinc-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 lg:py-20 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-1 text-[10px] font-mono tracking-[0.25em] uppercase mb-6 fade-up">
              <span className="w-2 h-2 bg-[#FFC60A] inline-block" /> SaaS B2B · Transport Scolaire
            </div>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.92] fade-up" style={{ animationDelay: "60ms" }}>
              Le cerveau<br /> logistique<br /> de vos <span className="bg-[#FFC60A] px-2 -mx-1">navettes</span>.
            </h1>
            <p className="mt-6 max-w-xl text-zinc-600 text-base sm:text-lg fade-up" style={{ animationDelay: "140ms" }}>
              Planifiez, optimisez et supervisez chaque trajet. Affectez le bon véhicule automatiquement selon le nombre d'enfants. Donnez aux parents la sérénité du temps réel.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 fade-up" style={{ animationDelay: "200ms" }}>
              <Link to="/register" data-testid="hero-primary-cta" className="inline-flex items-center gap-2 bg-[#FFC60A] border border-zinc-900 px-6 py-3 font-bold hover:translate-y-[-2px] transition-transform">
                Essai gratuit 14 jours <ArrowRight size={16} />
              </Link>
              <Link to="/login" data-testid="hero-secondary-cta" className="inline-flex items-center gap-2 border border-zinc-900 px-6 py-3 font-semibold hover:bg-zinc-100">
                Démo en direct
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-0 border-t border-zinc-900">
              <Stat label="Tournées optimisées" value="−32%" />
              <Stat label="Anomalies détectées" value="< 8s" />
              <Stat label="Parents rassurés" value="100%" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative h-[460px] border border-zinc-900 overflow-hidden bg-zinc-900">
              <img
                src="https://images.unsplash.com/photo-1754573458272-e24d7ad5d2c6?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
                alt="Bus scolaire"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/70 via-zinc-900/10 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
                <span className="bg-white border border-zinc-900 px-3 py-1 text-[10px] font-mono tracking-[0.2em] uppercase">Temps réel</span>
                <span className="bg-[#FFC60A] border border-zinc-900 px-3 py-1 text-[10px] font-mono tracking-[0.2em] uppercase">QR Scan</span>
                <span className="bg-white border border-zinc-900 px-3 py-1 text-[10px] font-mono tracking-[0.2em] uppercase">GPS</span>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden sm:block border border-zinc-900 bg-white p-4 w-56">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">LIVE — 07:42</div>
              <div className="font-display font-bold text-lg mt-1">Bus #AB-123-CD</div>
              <div className="text-xs text-zinc-600">6 / 16 enfants à bord</div>
              <div className="mt-2 h-1 bg-zinc-200">
                <div className="h-1 bg-[#FFC60A]" style={{ width: "38%" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-b border-zinc-900 bg-zinc-900 text-white overflow-hidden">
        <div className="flex whitespace-nowrap py-4 marquee-track font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-12 pr-12">
              <span>OPTIMISÉ</span><span className="text-[#FFC60A]">●</span>
              <span>SÉCURISÉ</span><span className="text-[#FFC60A]">●</span>
              <span>TEMPS RÉEL</span><span className="text-[#FFC60A]">●</span>
              <span>SCALABLE</span><span className="text-[#FFC60A]">●</span>
              <span>PREMIUM</span><span className="text-[#FFC60A]">●</span>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="border-b border-zinc-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">01 / MODULES</div>
              <h2 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tighter mt-2">10 modules.<br />1 plateforme.</h2>
            </div>
            <p className="max-w-sm text-zinc-600 text-sm">De la réservation parent à la validation chauffeur, tout est connecté.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
            <Feature icon={Cpu} title="Affectation automatique" desc="L'algorithme choisit le plus petit véhicule adapté au nombre d'enfants. Zéro tableur." span="md:col-span-6" dark />
            <Feature icon={Radar} title="Optimisation de tournée" desc="Nearest-neighbor géolocalisé. Moins de kilomètres, moins de temps, moins de CO₂." span="md:col-span-3" />
            <Feature icon={QrCode} title="Identification QR" desc="Chaque enfant a son code unique. Scan = montée validée." span="md:col-span-3" />
            <Feature icon={MapPin} title="Suivi temps réel" desc="Position du bus, ETA, et progression par arrêt." span="md:col-span-4" />
            <Feature icon={BellRing} title="Notifications parents" desc="Montée, descente, anomalie — les parents sont prévenus." span="md:col-span-4" />
            <Feature icon={ShieldCheck} title="Alertes anomalies" desc="Retard, déviation, enfant manquant — l'admin est alerté." span="md:col-span-4" />
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section id="roles" className="border-b border-zinc-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-3 gap-0 border border-zinc-900">
          {[
            { title: "Admin", desc: "Dashboard, flotte, planning, optimisation, KPI.", img: "https://images.pexels.com/photos/14446732/pexels-photo-14446732.jpeg?auto=compress&cs=tinysrgb&w=900" },
            { title: "Chauffeur", desc: "Mission du jour, navigation, scan QR, validation.", img: "https://images.unsplash.com/photo-1674730280115-ee2049ca3360?crop=entropy&cs=srgb&fm=jpg&w=900&q=85" },
            { title: "Parent", desc: "Réservation, QR de l'enfant, live tracking, alertes.", img: "https://images.unsplash.com/photo-1758782213616-7b4cd41eff29?crop=entropy&cs=srgb&fm=jpg&w=900&q=85" },
          ].map((r, i) => (
            <div key={i} className="border-r border-zinc-900 last:border-r-0">
              <div className="relative h-48 border-b border-zinc-900 overflow-hidden">
                <img src={r.img} alt={r.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">ROLE 0{i + 1}</div>
                <h3 className="font-display font-black text-3xl tracking-tighter mt-1">{r.title}</h3>
                <p className="mt-2 text-sm text-zinc-600">{r.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="border-b border-zinc-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">02 / FLOW</div>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tighter mt-2 mb-10">Un flux opérationnel, de bout en bout.</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-zinc-900">
            {[
              ["01", "Réservation", "Les parents réservent la place de leur enfant."],
              ["02", "Optimisation", "Les tournées sont regroupées et ordonnancées."],
              ["03", "Affectation", "Véhicule + chauffeur attribués automatiquement."],
              ["04", "Trajet", "QR scan, suivi live, notifications temps réel."],
            ].map(([n, t, d]) => (
              <div key={n} className="p-6 border-r border-zinc-900 last:border-r-0">
                <div className="font-mono text-xs tracking-[0.3em] text-zinc-500">{n}</div>
                <h4 className="mt-3 font-display font-bold text-xl">{t}</h4>
                <p className="mt-2 text-sm text-zinc-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="cta" className="border-b border-zinc-900 bg-[#FFC60A]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24 text-center">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase">03 / DÉMARRER</div>
          <h2 className="font-display font-black uppercase text-5xl sm:text-6xl tracking-tighter mt-2 leading-none">
            Prêts à rouler?
          </h2>
          <p className="mt-4 max-w-md mx-auto text-zinc-800">Essai gratuit 14 jours · Sans carte bancaire · Support FR.</p>
          <Link to="/register" data-testid="footer-cta-register" className="mt-6 inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3 font-bold border border-zinc-900 hover:bg-white hover:text-zinc-900">
            Créer mon compte <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-zinc-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FFC60A] flex items-center justify-center">
              <Bus size={16} className="text-zinc-900" />
            </div>
            <span className="font-display font-black tracking-tighter">LOGIBUS</span>
          </div>
          <p className="text-xs text-zinc-400 font-mono tracking-widest uppercase">© 2026 LOGIBUS · Made with yellow paint.</p>
        </div>
      </footer>
    </div>
  );
}
